import { useEffect, useState } from "react";
import api from "../lib/api";
import PageHeader from "../components/PageHeader";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../components/ui/dialog";
import { Badge } from "../components/ui/badge";
import { Plus, Printer, Trash2, IdCard, Upload, Pencil, User as UserIcon } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "../contexts/AuthContext";
import { printMemberCard } from "../lib/documents";

const TYPE_BADGE = {
  joueur: "bg-[#C62828]/10 text-[#C62828] border-[#C62828]/30",
  employe: "bg-[#1976D2]/10 text-[#1976D2] border-[#1976D2]/30",
  supporter: "bg-[#FBC02D]/10 text-[#F57F17] border-[#FBC02D]/40",
  dirigeant: "bg-[#7B1FA2]/10 text-[#7B1FA2] border-[#7B1FA2]/30",
};
const empty = {
  first_name: "", last_name: "", type: "supporter", ref_id: "", photo_url: "",
  date_of_birth: "", phone: "", email: "",
  member_since: new Date().toISOString().slice(0, 10), expires_at: "",
};

export default function Members() {
  const { user } = useAuth();
  const [list, setList] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(empty);
  const [uploading, setUploading] = useState(false);

  const load = () => api.get("/members").then(({ data }) => setList(data));
  useEffect(() => { load(); }, []);

  const photoSrc = (url) => {
    if (!url) return null;
    if (url.startsWith("http")) return url;
    const token = localStorage.getItem("asc_token");
    return `${process.env.REACT_APP_BACKEND_URL}${url}?auth=${token}`;
  };

  const openCreate = () => {
    setEditing(null);
    setForm(empty);
    setOpen(true);
  };

  const openEdit = (m) => {
    setEditing(m);
    setForm({
      first_name: m.first_name || "",
      last_name: m.last_name || "",
      type: m.type || "supporter",
      ref_id: m.ref_id || "",
      photo_url: m.photo_url || "",
      date_of_birth: m.date_of_birth || "",
      phone: m.phone || "",
      email: m.email || "",
      member_since: m.member_since ? m.member_since.slice(0, 10) : new Date().toISOString().slice(0, 10),
      expires_at: m.expires_at ? m.expires_at.slice(0, 10) : "",
    });
    setOpen(true);
  };

  const onPhoto = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/uploads", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setForm((f) => ({ ...f, photo_url: data.url }));
      toast.success("Photo téléchargée");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erreur upload");
    } finally {
      setUploading(false);
    }
  };

  const submit = async (e) => {
    e.preventDefault();
    const payload = {
      ...form,
      ref_id: form.ref_id || null,
      date_of_birth: form.date_of_birth || null,
      expires_at: form.expires_at || null,
    };
    try {
      if (editing) {
        await api.patch(`/members/${editing.id}`, payload);
        toast.success("Membre modifié");
      } else {
        await api.post("/members", payload);
        toast.success("Membre créé");
      }
      setOpen(false); setEditing(null); setForm(empty); load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erreur");
    }
  };

  const onDel = async (id) => {
    if (!confirm("Supprimer ?")) return;
    await api.delete(`/members/${id}`);
    load();
  };

  return (
    <div>
      <PageHeader
        overline="Communauté"
        title="Cartes de membres"
        description="Joueurs, employés, supporters et dirigeants."
        actions={
          <Button onClick={openCreate} data-testid="create-member-btn" className="btn-club">
            <Plus className="w-4 h-4 mr-2" /> Nouveau membre
          </Button>
        }
      />

      {list.length === 0 ? (
        <div className="bg-white border border-dashed border-[#E0E0E0] rounded-md p-12 text-center">
          <IdCard className="w-10 h-10 text-[#C62828]/40 mx-auto mb-4" />
          <h3 className="font-display text-xl font-bold">Aucun membre</h3>
          <p className="text-sm text-[#616161] mt-2">Ajoutez votre premier membre pour générer sa carte.</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="members-grid">
          {list.map((m) => (
            <div key={m.id} className="bg-white border border-[#E0E0E0] rounded-md overflow-hidden hover:border-[#C62828]/40 transition" data-testid={`member-card-${m.id}`}>
              <div className="bg-gradient-to-br from-[#9E1B1B] to-[#C62828] text-white p-4">
                <p className="text-[10px] tracking-overline text-white/70">Carte membre</p>
                <p className="font-display font-bold text-lg mt-1">{user?.club?.name}</p>
              </div>
              <div className="p-4 flex items-center gap-3">
                <div className="w-14 h-14 rounded bg-[#F4F5F7] flex items-center justify-center font-display font-bold text-[#C62828] overflow-hidden border border-[#E0E0E0]">
                  {m.photo_url ? (
                    <img src={photoSrc(m.photo_url)} alt={`${m.first_name} ${m.last_name}`} className="w-full h-full object-cover" />
                  ) : (
                    <span>{m.first_name?.[0]}{m.last_name?.[0]}</span>
                  )}
                </div>
                <div className="flex-1 min-w-0">
                  <p className="font-semibold truncate">{m.first_name} {m.last_name}</p>
                  <p className="text-xs font-mono text-[#C62828] font-semibold">{m.member_number}</p>
                  <Badge variant="outline" className={`mt-1 text-[10px] ${TYPE_BADGE[m.type]} border capitalize`}>{m.type}</Badge>
                </div>
              </div>
              <div className="px-4 pb-3 text-xs text-[#616161]">
                Depuis {new Date(m.member_since).toLocaleDateString("fr-FR")}
              </div>
              <div className="px-4 py-2 border-t border-[#E0E0E0] flex justify-end gap-1">
                <Button size="sm" variant="ghost" onClick={() => openEdit(m)} data-testid={`edit-member-${m.id}`}><Pencil className="w-3.5 h-3.5" /></Button>
                <Button size="sm" variant="ghost" onClick={() => printMemberCard(user.club, m)} data-testid={`print-member-${m.id}`}><Printer className="w-3.5 h-3.5" /></Button>
                <Button size="sm" variant="ghost" onClick={() => onDel(m.id)}><Trash2 className="w-3.5 h-3.5 text-[#C62828]" /></Button>
              </div>
            </div>
          ))}
        </div>
      )}

      <Dialog open={open} onOpenChange={(v) => { setOpen(v); if (!v) { setEditing(null); setForm(empty); } }}>
        <DialogContent className="max-w-md max-h-[90vh] overflow-y-auto" data-testid="member-dialog">
          <DialogHeader>
            <DialogTitle className="font-display">{editing ? "Modifier le membre" : "Nouveau membre"}</DialogTitle>
          </DialogHeader>
          <form onSubmit={submit} className="space-y-3">
            <div className="flex items-center gap-4">
              <div className="w-20 h-20 rounded-md bg-[#F4F5F7] border border-[#E0E0E0] overflow-hidden flex items-center justify-center">
                {form.photo_url ? (
                  <img src={photoSrc(form.photo_url)} alt="" className="w-full h-full object-cover" data-testid="member-photo-preview" />
                ) : (
                  <UserIcon className="w-6 h-6 text-[#616161]" />
                )}
              </div>
              <label className="cursor-pointer">
                <input type="file" accept="image/*" className="hidden" onChange={onPhoto} data-testid="member-photo-input" />
                <Button type="button" variant="outline" disabled={uploading} asChild>
                  <span><Upload className="w-4 h-4 mr-2" /> {uploading ? "Envoi..." : "Photo"}</span>
                </Button>
              </label>
              {form.photo_url && (
                <Button type="button" variant="ghost" size="sm" onClick={() => setForm((f) => ({ ...f, photo_url: "" }))} data-testid="member-photo-remove">
                  Retirer
                </Button>
              )}
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div><Label>Prénom *</Label><Input required value={form.first_name} onChange={(e) => setForm({ ...form, first_name: e.target.value })} data-testid="member-firstname" /></div>
              <div><Label>Nom *</Label><Input required value={form.last_name} onChange={(e) => setForm({ ...form, last_name: e.target.value })} data-testid="member-lastname" /></div>
              <div className="col-span-2">
                <Label>Type *</Label>
                <Select value={form.type} onValueChange={(v) => setForm({ ...form, type: v })}>
                  <SelectTrigger data-testid="member-type"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="joueur">Joueur</SelectItem>
                    <SelectItem value="employe">Employé</SelectItem>
                    <SelectItem value="supporter">Supporter</SelectItem>
                    <SelectItem value="dirigeant">Dirigeant</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div><Label>Membre depuis *</Label><Input type="date" required value={form.member_since} onChange={(e) => setForm({ ...form, member_since: e.target.value })} /></div>
              <div><Label>Expire le</Label><Input type="date" value={form.expires_at} onChange={(e) => setForm({ ...form, expires_at: e.target.value })} /></div>
              <div><Label>Téléphone</Label><Input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} /></div>
              <div><Label>Email</Label><Input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} /></div>
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setOpen(false)}>Annuler</Button>
              <Button type="submit" className="btn-club" data-testid="member-submit-btn">{editing ? "Enregistrer" : "Créer"}</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
